
import React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Clock, Calendar as CalendarIcon, Video, Phone, 
  Plus, ChevronRight 
} from 'lucide-react';
import { format, isSameDay } from 'date-fns';

// Define the local appointment interface for consistency
interface LocalAppointment {
  id: string;
  patientId: string;
  doctorId: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: string;
  status: string;
  duration: number;
  notes?: string;
  patientName?: string;
  date?: string;
  type?: string;
  time?: string;
}

interface AppointmentCalendarProps {
  appointments: LocalAppointment[];
  onCreateAppointment: (date?: Date) => void;
  onViewDetails: (appointmentId: string) => void;
  onReschedule: (id: string, newDate?: Date) => void;
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  appointments,
  onCreateAppointment,
  onViewDetails,
  onReschedule
}) => {
  // Function to convert appointment strings to Date objects for comparison
  const getAppointmentDate = (appointment: LocalAppointment): Date => {
    if (appointment.date) {
      return new Date(appointment.date);
    }
    
    const dateStr = appointment.appointmentDate;
    const timeStr = appointment.appointmentTime || appointment.time || '00:00';
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date(dateStr);
    date.setHours(hours, minutes);
    
    return date;
  };
  
  // Function to check if a date has appointments
  const hasAppointmentsOnDate = (date: Date): boolean => {
    return appointments.some(appointment => 
      isSameDay(getAppointmentDate(appointment), date)
    );
  };
  
  // Function to get appointments for a specific date
  const getAppointmentsForDate = (date: Date): LocalAppointment[] => {
    return appointments.filter(appointment => 
      isSameDay(getAppointmentDate(appointment), date)
    );
  };
  
  // Function to get appointment type icon
  const getAppointmentTypeIcon = (type: string) => {
    switch(type) {
      case 'video':
        return <Video className="h-4 w-4 text-blue-500" />;
      case 'voice':
        return <Phone className="h-4 w-4 text-purple-500" />;
      default:
        return <CalendarIcon className="h-4 w-4 text-green-500" />;
    }
  };
  
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(new Date());
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1">
        <Card>
          <CardContent className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
              modifiersStyles={{
                selected: { 
                  backgroundColor: 'var(--primary)', 
                  color: 'white'
                }
              }}
              modifiers={{
                hasAppointment: (date) => hasAppointmentsOnDate(date)
              }}
            />
            
            <Button 
              className="w-full mt-4"
              onClick={() => onCreateAppointment(selectedDate)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Appointment
            </Button>
          </CardContent>
        </Card>
      </div>
      
      <div className="lg:col-span-2">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">
                {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Select a date'}
              </h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onCreateAppointment(selectedDate)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
            
            <div className="space-y-3">
              {selectedDate && getAppointmentsForDate(selectedDate).length > 0 ? (
                getAppointmentsForDate(selectedDate).map((appointment) => (
                  <div 
                    key={appointment.id}
                    className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-primary/10 p-2 rounded-full">
                        {getAppointmentTypeIcon(appointment.type || appointment.appointmentType)}
                      </div>
                      <div>
                        <p className="font-medium">{appointment.patientName}</p>
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Clock className="h-3 w-3 mr-1" />
                          {appointment.appointmentTime || appointment.time}
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => onViewDetails(appointment.id)}
                    >
                      Details
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center border rounded-md bg-muted/10">
                  <p className="text-muted-foreground">No appointments for this date</p>
                  <Button 
                    className="mt-4" 
                    variant="outline"
                    onClick={() => onCreateAppointment(selectedDate)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Appointment
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppointmentCalendar;
